import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-sans font-semibold text-xl text-foreground mb-4 tracking-[-0.01em]">{title}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-foreground/75">{children}</div>
    </section>
  );
}

function P({ children }) {
  return <p>{children}</p>;
}

function Ul({ items }) {
  return (
    <ul className="list-none space-y-2 pl-0">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span style={{ color: "#8A8A8A" }} className="mt-0.5 shrink-0">—</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicy() {
  const updated = "June 2026 (rev. 2)";

  return (
    <div className="min-h-screen bg-ink text-white">
      <Navigation />
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at top, rgba(138,138,138,0.06) 0%, transparent 50%)" }}
      />

      <div className="relative z-10 pt-32 pb-24 px-6 max-w-3xl mx-auto">
        {/* Header */}
        <p className="overline mb-4">Legal</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-tight mb-3">
          Privacy <span className="gold-gradient-text italic">Policy</span>
        </h1>
        <p className="text-foreground/50 text-sm mb-12">Last updated: {updated}</p>

        <div className="card-luxe p-8 sm:p-10">

          <Section title="Who We Are">
            <P>
              Nisky Auto is a mobile car detailing service operating in and around Niskayuna,
              New York. We provide appointment-based detailing services each summer from June 18
              through September 6. This policy explains how we collect, use, and protect the
              personal information you share with us when booking or communicating with us.
            </P>
            <P>
              Questions about this policy can be directed to us through the booking status page
              or by contacting us at the email on your booking confirmation.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Information We Collect">
            <P>When you book a detailing appointment, we collect:</P>
            <Ul items={[
              "Full name — to address communications and identify your booking.",
              "Email address — to send booking confirmations, appointment reminders, and your invoice.",
              "Phone number (optional) — if provided, used only to contact you about your appointment.",
              "Service address — the location where you want the detail performed. This is stored as part of your booking record.",
              "Vehicle information — make, model, body type, model year (optional), and your assessment of the vehicle's condition. Used to calculate pricing and prepare for your appointment.",
              "Appointment date and time — to schedule and manage your booking.",
              "Booking reference — a unique identifier generated for your booking, used to access your status page.",
            ]} />
            <P>
              We do not collect or store any payment information. All payments are made in cash
              or in person at the time of service. No credit card numbers, bank details, or
              payment tokens are ever processed or stored by us.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Automatically Collected Information">
            <P>
              When you interact with our booking platform, our servers automatically collect certain
              technical information to protect the security and integrity of our service:
            </P>
            <Ul items={[
              "IP address — recorded with every booking action (starting a booking, confirming, cancelling, sending a message, or requesting a reschedule). We use IP addresses to enforce rate limits, detect and block abusive or automated traffic, and investigate security incidents. IP addresses are stored in our security logs in Firebase.",
              "Request metadata — timestamps, action type, and booking reference associated with each request. This is retained as part of our security audit log.",
            ]} />
            <P>
              IP addresses are not used for advertising, tracking across other websites, or any
              purpose other than security and abuse prevention. They may be retained for up to
              12 months as part of our security logs, after which they are removed.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="How We Use Your Information">
            <Ul items={[
              "To create and manage your booking record.",
              "To send you a booking confirmation and, upon completion, an invoice by email.",
              "To allow you to check your booking status and communicate with us through the status page.",
              "To reschedule or cancel your appointment if needed.",
              "To notify you of any changes to your appointment.",
              "Internally, to calculate worker payroll and job assignments — your name and vehicle details are visible to our service team.",
              "To enforce rate limits and protect the platform against abuse — your IP address is logged with each booking action and reviewed by our team if suspicious activity is detected.",
            ]} />
            <P>
              We do not use your information for advertising, marketing campaigns, or any purpose
              beyond delivering and managing your detailing service.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Third-Party Services">
            <P>
              We rely on the following third-party services to operate. Each has its own privacy
              policy governing how they handle data:
            </P>
            <Ul items={[
              "Firebase (Google) — we use Firebase Authentication to create a secure login tied to your booking reference, and Firebase Realtime Database to store and sync your booking status. Your booking data is stored on Google's infrastructure. See: firebase.google.com/support/privacy",
              "MongoDB Atlas — our primary database where booking records, job assignments, and internal records are stored. Hosted by MongoDB, Inc. See: mongodb.com/legal/privacy-policy",
              "Brevo (formerly Sendinblue) — the email delivery service we use to send confirmations and invoices. Your email address and name are transmitted to Brevo to deliver these messages. See: brevo.com/legal/privacypolicy",
              "Google Places API — used to provide address autocomplete when you enter your service location during booking. Your partial address query is sent to Google to return suggestions. See: policies.google.com/privacy",
              "NHTSA Vehicle API — used to look up vehicle makes and models by name. No personal information is sent to this service; only vehicle make/model strings are queried.",
              "Render — the cloud platform that hosts our backend server. Your requests pass through Render's infrastructure. See: render.com/privacy",
            ]} />
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Cookies and Tracking">
            <P>
              We do not use advertising cookies, tracking pixels, or analytics tools (such as
              Google Analytics). We do not build profiles of your browsing behavior or share
              data with advertisers.
            </P>
            <P>
              Firebase may use session tokens stored in your browser to keep you logged in to
              your booking status page. These are functional only and are not used for tracking.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Data Retention">
            <P>
              Your booking information is retained for as long as it is needed for service and
              accounting purposes. Completed and cancelled bookings may be manually removed from
              our systems by our team at any time. There is no automated deletion schedule.
            </P>
            <P>
              When a booking record is deleted, the associated login account and all stored data
              are permanently removed from our databases and from Firebase.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Your Rights">
            <P>
              You have the right to request access to the information we hold about you, and
              to request that it be corrected or deleted. To make a request, contact us through
              the booking status page using your booking reference, or reply to any email we
              have sent you.
            </P>
            <P>
              If you are a resident of California, you may have additional rights under the
              California Consumer Privacy Act (CCPA), including the right to know what personal
              information is collected, to opt out of its sale (we do not sell personal
              information), and to request deletion.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Children's Privacy">
            <P>
              Our services are not directed at individuals under the age of 13. We do not
              knowingly collect personal information from children. If you believe a child has
              submitted information to us, please contact us and we will promptly delete it.
            </P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="Changes to This Policy">
            <P>
              We may update this privacy policy from time to time. When we do, the "last
              updated" date at the top of this page will change. Continued use of our booking
              service after any update constitutes acceptance of the revised policy.
            </P>
          </Section>

        </div>

        <p className="text-center text-xs text-foreground/30 mt-8">
          © {new Date().getFullYear()} Nisky Auto · Niskayuna, NY ·{" "}
          <Link to="/" className="hover:text-foreground/60 underline">Home</Link>
        </p>
      </div>

      <Footer />
    </div>
  );
}
